import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, Compass, Sparkles, Grid3x3, Flame } from "lucide-react";
import { api } from "@/lib/apiClient";

const GOLD = "#C9A84C";

const LO_SHU_LAYOUT = [
  [4, 9, 2],
  [3, 5, 7],
  [8, 1, 6],
];

function Section({ children, delay = 0, testid }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      data-testid={testid}
      className="mb-20"
    >
      {children}
    </motion.section>
  );
}

export default function DiagnosticSouverainete() {
  const [params] = useSearchParams();
  const leadId = params.get("id");
  const [diag, setDiag] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Empêcher l'indexation
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (!leadId) {
      setError("Identifiant de diagnostic manquant.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/diagnostic/${encodeURIComponent(leadId)}`);
        setDiag(data.diagnostic);
        document.title = `Code Souverain de ${data.diagnostic.prenom_principal} | Académie AETHERIS`;
        // Enrichissement IA automatique en arrière-plan si absent
        if (!data.diagnostic.synthese_ai) {
          api.post(`/ai/diagnostic/${encodeURIComponent(leadId)}/enrich`)
            .then((res) => {
              if (res?.data?.synthese_ai) {
                setDiag((d) => ({ ...d, synthese_ai: res.data.synthese_ai }));
              }
            })
            .catch(() => { /* fallback vers synthese template déjà affichée */ });
        }
      } catch (e) {
        setError("Diagnostic introuvable. Merci de refaire votre demande.");
      } finally {
        setLoading(false);
      }
    })();
  }, [leadId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-ink" data-testid="diag-loading">
        <Loader2 className="animate-spin" size={36} style={{ color: GOLD }} />
      </div>
    );
  }

  if (error || !diag) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-ink px-6" data-testid="diag-error">
        <div className="text-center max-w-md">
          <p className="text-white/70 mb-6">{error || "Diagnostic introuvable."}</p>
          <Link
            to="/bilan"
            className="inline-block px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold"
            style={{ backgroundColor: GOLD, color: "#0a0a0a" }}
          >
            Refaire ma demande
          </Link>
        </div>
      </div>
    );
  }

  const num = diag.numerologie;
  const kua = diag.kua;
  const ls = diag.lo_shu;

  return (
    <div className="min-h-screen bg-brand-ink text-white pt-24 pb-24" data-testid="diagnostic-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <div data-testid="diag-verification-notice" className="mb-10 border border-gold/40 bg-gold/5 px-6 py-5 text-sm leading-relaxed text-white/80">
          <span className="text-gold font-semibold">Un email de confirmation vous a été envoyé.</span>{" "}
          Veuillez cliquer sur le lien dans cet email pour valider votre adresse et recevoir votre diagnostic personnalisé. Pensez à vérifier vos spams.
        </div>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-24"
        >
          <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>
            Académie AETHERIS · Diagnostic Inaugural
          </p>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95]">
            Le Code Souverain de<br />
            <span style={{ color: GOLD }}>{diag.prenom_principal}</span>
          </h1>
          <p className="text-white/50 text-sm mt-6">
            Né(e) le {diag.birthdate} · Méthode Triple Fusion™ (Pythagore · Lo Shu · IFÀ)
          </p>
        </motion.header>

        {/* Numérologie */}
        <Section testid="section-numerologie" delay={0.05}>
          <div className="flex items-center gap-3 mb-8">
            <Sparkles size={18} style={{ color: GOLD }} />
            <p className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>Numérologie Pythagoricienne</p>
          </div>

          <div className="mb-10 p-8 border" style={{ borderColor: `${GOLD}44`, background: "rgba(201,168,76,0.04)" }}>
            <p className="text-xs uppercase tracking-luxe text-white/50 mb-3">Chemin de Vie</p>
            <div className="flex items-baseline gap-6 mb-4">
              <div className="font-serif text-[110px] leading-none" style={{ color: GOLD }} data-testid="cdv-number">
                {num.chemin_de_vie.nombre}
              </div>
              <div>
                <div className="font-serif text-2xl md:text-3xl">{num.chemin_de_vie.titre}</div>
              </div>
            </div>
            <p className="text-white/75 text-base md:text-lg leading-relaxed font-light">{num.chemin_de_vie.texte}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { label: "Expression", data: num.expression, sub: "Comment vous vous exprimez" },
              { label: "Nombre de l'Âme", data: num.ame, sub: "Ce que vous désirez au fond" },
              { label: "Réalisation", data: num.realisation, sub: "Comment vous êtes perçu(e)" },
            ].map((n) => (
              <div key={n.label} className="p-6 border border-white/10 bg-brand-surface/30" data-testid={`num-${n.label}`}>
                <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-1">{n.label}</p>
                <div className="font-serif text-5xl mb-2" style={{ color: GOLD }}>{n.data.nombre}</div>
                <p className="text-white/70 text-sm leading-relaxed">{n.data.texte}</p>
                <p className="text-[11px] text-white/30 mt-2 italic">{n.sub}</p>
              </div>
            ))}
          </div>

          {num.lignee && (
            <div className="mt-8 p-5 border-l-2 bg-brand-surface/20" style={{ borderColor: GOLD }}>
              <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-1">Vibration de lignée · nom de jeune fille</p>
              <div className="flex items-baseline gap-4">
                <div className="font-serif text-3xl" style={{ color: GOLD }}>{num.lignee.expression}</div>
                <p className="text-white/60 text-sm italic">{num.lignee.note}</p>
              </div>
            </div>
          )}
        </Section>

        {/* Lo Shu */}
        <Section testid="section-lo-shu" delay={0.05}>
          <div className="flex items-center gap-3 mb-8">
            <Grid3x3 size={18} style={{ color: GOLD }} />
            <p className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>Grille Lo Shu</p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <div className="grid grid-cols-3 gap-3 max-w-sm" data-testid="lo-shu-grid">
                {LO_SHU_LAYOUT.flat().map((n) => {
                  const count = ls.grid[String(n)] || 0;
                  const present = count > 0;
                  return (
                    <div
                      key={n}
                      className={`aspect-square flex flex-col items-center justify-center border transition-colors ${
                        present ? "" : "opacity-30"
                      }`}
                      style={{
                        borderColor: present ? GOLD : "rgba(255,255,255,0.12)",
                        background: present ? "rgba(201,168,76,0.12)" : "transparent",
                      }}
                      data-testid={`lo-shu-cell-${n}`}
                    >
                      <div className="font-serif text-4xl" style={{ color: present ? GOLD : "rgba(255,255,255,0.35)" }}>{n}</div>
                      {present && <div className="text-[10px] uppercase tracking-luxe text-white/40 mt-1">×{count}</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] mb-3" style={{ color: GOLD }}>Forces natives</p>
                <ul className="space-y-2 text-sm">
                  {ls.present.map((p) => (
                    <li key={p.chiffre} className="flex gap-3">
                      <span className="font-serif text-lg shrink-0" style={{ color: GOLD }}>{p.chiffre}</span>
                      <span className="text-white/70 leading-relaxed">{p.texte}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-3">Zones de travail</p>
                <ul className="space-y-2 text-sm">
                  {ls.absent.length === 0 && <li className="italic text-white/40">Grille pleine — architecture rare.</li>}
                  {ls.absent.map((a) => (
                    <li key={a.chiffre} className="flex gap-3">
                      <span className="font-serif text-lg shrink-0 text-white/30">{a.chiffre}</span>
                      <span className="text-white/50 leading-relaxed">{a.texte}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <p className="text-[11px] uppercase tracking-[0.15em] mb-4" style={{ color: GOLD }}>Flèches actives</p>
            {ls.arrows.length === 0 ? (
              <p className="text-white/50 italic text-sm">
                Aucune flèche complète n'est active. C'est votre chantier prioritaire d'alignement —
                le Coaching Élite vous guide dans cette activation.
              </p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {ls.arrows.map((a) => (
                  <div key={a.nom} className="p-4 border border-white/10 bg-brand-surface/20" data-testid={`arrow-${a.nom}`}>
                    <p className="font-serif text-lg mb-1" style={{ color: GOLD }}>{a.nom}</p>
                    <p className="text-[10px] uppercase tracking-luxe text-white/40 mb-2">{a.chiffres.join(" · ")}</p>
                    <p className="text-white/70 text-sm leading-relaxed">{a.texte}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* Kua */}
        <Section testid="section-kua" delay={0.05}>
          <div className="flex items-center gap-3 mb-8">
            <Compass size={18} style={{ color: GOLD }} />
            <p className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>Votre Nombre Kua (Feng Shui)</p>
          </div>

          <div className="p-8 border" style={{ borderColor: `${GOLD}44`, background: "rgba(201,168,76,0.04)" }}>
            <div className="flex items-baseline gap-6 mb-6">
              <div className="font-serif text-[100px] leading-none" style={{ color: GOLD }} data-testid="kua-number">
                {kua.nombre}
              </div>
              <div>
                <div className="font-serif text-2xl md:text-3xl">{kua.titre}</div>
                <p className="text-white/50 text-sm mt-1">
                  Élément <span style={{ color: GOLD }}>{kua.element}</span> · Groupe <span style={{ color: GOLD }}>{kua.groupe}</span>
                </p>
              </div>
            </div>
            <p className="text-white/75 text-base leading-relaxed font-light mb-6">{kua.texte}</p>

            <div>
              <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-3">Vos 4 directions favorables</p>
              <div className="flex flex-wrap gap-3">
                {kua.directions_favorables.map((d) => (
                  <span
                    key={d}
                    className="px-4 py-2 text-sm border"
                    style={{ borderColor: GOLD, color: GOLD, background: "rgba(201,168,76,0.08)" }}
                    data-testid={`kua-direction-${d}`}
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Synthèse */}
        <Section testid="section-synthese" delay={0.05}>
          <div className="p-10 border relative" style={{ borderColor: GOLD, background: "linear-gradient(180deg, rgba(201,168,76,0.06) 0%, transparent 100%)" }}>
            <Flame size={20} style={{ color: GOLD }} className="mb-4" />
            <p className="text-xs uppercase tracking-[0.2em] mb-6" style={{ color: GOLD }}>
              Synthèse {diag.synthese_ai ? "personnalisée par Rodrigue" : "unificatrice"}
            </p>
            <p className="font-serif text-xl md:text-2xl leading-relaxed italic text-white/90 whitespace-pre-line">
              {diag.synthese_ai || diag.synthese}
            </p>
          </div>
        </Section>

        {/* CTA */}
        <Section testid="section-cta" delay={0.05}>
          <div className="text-center px-6 py-16 border" style={{ borderColor: `${GOLD}55`, background: "rgba(0,0,0,0.4)" }}>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Pour aller plus loin</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-6 leading-tight">
              Ce diagnostic est une photo.<br />
              <span style={{ color: GOLD }}>Le Coaching Élite est le film.</span>
            </h2>
            <p className="text-white/60 text-lg font-light max-w-2xl mx-auto mb-10 leading-relaxed">
              Rodrigue CASSILDÉ vous accompagne personnellement pour activer votre Code Souverain
              sur 12 séances individuelles — décoder, réorienter, incarner.
            </p>

            {/* Calendly — Appel de sélection gratuit (option sobre) */}
            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.15em] text-white/50 mb-3">
                Vous avez des questions sur vos résultats ?
              </p>
              <a
                href="https://calendly.com/r-cassilde/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="diag-cta-calendly"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-xs uppercase tracking-[0.15em] font-semibold border transition-colors"
                style={{ borderColor: GOLD, color: GOLD, background: "rgba(201,168,76,0.06)" }}
              >
                Réserver mon Appel de Sélection — GRATUIT <ArrowRight size={16} />
              </a>
            </div>

            {/* Coaching Élite — bouton principal or plein */}
            <Link
              to="/coaching-elite"
              data-testid="diag-cta-elite"
              className="inline-flex items-center gap-3 px-10 py-5 text-sm uppercase tracking-[0.15em] font-semibold transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: GOLD, color: "#0a0a0a" }}
            >
              Je veux aller plus loin — Coaching Élite <ArrowRight size={18} />
            </Link>
            <p style={{ color: '#C9A84C', fontSize: '0.8rem', textAlign: 'center', marginTop: '6px', fontStyle: 'italic' }}>
              Paiement en 3 fois sans frais disponible
            </p>
            <p className="text-white/40 text-xs mt-6">
              Votre diagnostic complet vient aussi de vous être envoyé par email.
            </p>
          </div>
        </Section>
      </div>
    </div>
  );
}
